import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { HelpCircle, Search, ThumbsUp, ExternalLink, Lightbulb, BookOpen, Zap, ArrowLeft } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function FAQPage() {
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      const response = await axios.get(`${API}/faq`);
      setFaqs(response.data.faqs);
      setCategories(['all', ...response.data.categories]);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      toast.error("Failed to load FAQs");
    } finally {
      setLoading(false);
    }
  };

  const markHelpful = async (faqId) => {
    try {
      await axios.post(`${API}/faq/${faqId}/helpful`, {}, { withCredentials: true });
      toast.success("Thanks for your feedback!");
    } catch (error) {
      console.error('Error marking helpful:', error);
    }
  };

  const filteredFaqs = faqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getPriorityColor = (priority) => {
    const colors = {
      "high": "bg-red-500/10 text-red-500 border-red-500/30",
      "medium": "bg-amber-500/10 text-amber-500 border-amber-500/30",
      "low": "bg-blue-500/10 text-blue-500 border-blue-500/30"
    };
    return colors[priority] || colors.medium;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg gradient-active flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-semibold text-xl">QuantumFlowSync.ai</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate('/')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            <Button onClick={() => navigate('/login')}>
              Sign In
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12 max-w-6xl">
        <div className="space-y-6" data-testid="faq-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-semibold text-foreground flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                <HelpCircle className="w-6 h-6 text-white" />
              </div>
              Frequently Asked Questions
            </h1>
            <p className="text-muted-foreground mt-1">
              Find answers to common questions about QuantumFlowSync.ai
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => window.open('/wikihow', '_self')}
            className="flex items-center gap-2"
          >
            <BookOpen className="w-4 h-4" />
            Browse WikiHow Articles
          </Button>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search FAQs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                className="capitalize"
              >
                {category}
              </Button>
            ))}
          </div>
        </div>

        {/* FAQ List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-24 bg-muted/50 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filteredFaqs.length > 0 ? (
          <div className="space-y-4">
            {filteredFaqs.map((faq) => (
              <Card
                key={faq.id}
                className="glass-card border-border hover:border-primary/30 transition-all cursor-pointer"
                onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                data-testid={`faq-${faq.id}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="capitalize">
                          {faq.category}
                        </Badge>
                        {faq.priority && (
                          <Badge variant="outline" className={getPriorityColor(faq.priority)}>
                            {faq.priority}
                          </Badge>
                        )}
                      </div>
                      <CardTitle className="font-heading text-lg">
                        {faq.question}
                      </CardTitle>
                    </div>
                    <Lightbulb className={`w-5 h-5 transition-transform ${
                      expandedFaq === faq.id ? 'rotate-0 text-primary' : 'rotate-45 text-muted-foreground'
                    }`} />
                  </div>
                </CardHeader>
                
                {expandedFaq === faq.id && (
                  <CardContent>
                    <Separator className="mb-4" />
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      <p className="text-sm text-foreground whitespace-pre-wrap">
                        {faq.answer}
                      </p>
                    </div>
                    
                    {faq.screenshot && (
                      <div className="mt-4 p-3 bg-muted/30 rounded-lg border border-border">
                        <p className="text-xs font-medium text-muted-foreground mb-3">📸 Visual Guide:</p>
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${faq.screenshot}`}
                          alt={faq.question}
                          className="w-full rounded-lg border border-border"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <div 
                          className="hidden bg-muted/50 rounded p-4 text-center text-xs text-muted-foreground"
                        >
                          Screenshot: {faq.screenshot.split('/').pop()}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                      <p className="text-xs text-muted-foreground">
                        {faq.views || 0} views • {faq.helpful_count || 0} found this helpful
                      </p>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          markHelpful(faq.id);
                        }}
                        className="text-xs"
                        data-testid={`helpful-${faq.id}`}
                      >
                        <ThumbsUp className="w-4 h-4 mr-2" />
                        Helpful
                      </Button>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Search className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium text-foreground">No FAQs found</p>
            <p className="text-sm text-muted-foreground mt-2">
              Try adjusting your search or category filter
            </p>
          </div>
        )}

        {/* Help Section */}
        <Card className="glass-card border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-purple-500/5">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg text-foreground mb-2">
                  Can't find what you're looking for?
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Check out our community WikiHow articles for step-by-step guides, or create your own to help others!
                </p>
                <Button onClick={() => window.open('/wikihow', '_self')}>
                  <BookOpen className="w-4 h-4 mr-2" />
                  Explore WikiHow Articles
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
}
